import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Show } from '@side/pantry';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { getPlaceholderImage } from '@/common/utils/coverPhoto';
import { CARD_PHOTO_ASPECT } from './helpers';

type Props = {
  listingId: string;
  coverPhotoUrl: string | null;
  streetAddress: string;
  cityStateZip: string;
  mlsNumber: string | null;
  sellers: string;
  action?: ReactNode;
  loading: boolean;
  showMls?: boolean;
};

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid',
  borderColor: 'grey.400',
  borderRadius: '4px',
  backgroundColor: 'grey.100',
} as const;

const Loading = () => (
  <Box sx={cardSx}>
    <Skeleton
      variant="rectangular"
      sx={{
        width: '100%',
        aspectRatio: CARD_PHOTO_ASPECT,
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
      }}
    />
    <Stack sx={{ gap: 0.5, px: 2, pt: 1.5, pb: 2 }}>
      <Skeleton width="70%" height={20} />
      <Skeleton width="50%" height={16} />
      <Skeleton width="40%" height={16} />
    </Stack>
  </Box>
);

export const ListingSummaryCardMobile = ({
  listingId,
  coverPhotoUrl,
  streetAddress,
  cityStateZip,
  mlsNumber,
  sellers,
  action,
  loading,
  showMls = false,
}: Props) => {
  if (loading) return <Loading />;

  return (
    <Box sx={cardSx}>
      <Box sx={{ position: 'relative' }}>
        <Show
          when={coverPhotoUrl}
          fallback={
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: CARD_PHOTO_ASPECT,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                overflow: 'hidden',
                borderBottom: '0.5px solid',
                borderColor: 'grey.500',
                backgroundColor: 'common.white',
              }}
            >
              <Image
                src={getPlaceholderImage(listingId)}
                alt=""
                fill
                sizes="100vw"
                style={{ objectFit: 'contain' }}
              />
            </Box>
          }
        >
          {(url) => (
            // Cover photos are GCS signed URLs, excluded from the next/image
            // host allow-list, so render with a plain <img>. Height follows
            // the width at the photo's natural ratio so it's never cropped.
            <Box
              component="img"
              src={url}
              alt=""
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
              }}
            />
          )}
        </Show>
        <Show when={action}>
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>{action}</Box>
        </Show>
      </Box>

      <Stack sx={{ pt: 1.5 }}>
        <Stack sx={{ gap: 0.5, px: 2, pb: 1.25 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: 'text.primary', wordBreak: 'break-word' }}
          >
            {streetAddress}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {cityStateZip}
          </Typography>
          <Show when={showMls}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: 13 }}
            >
              MLS #{mlsNumber || <>&#8212;</>}
            </Typography>
          </Show>
        </Stack>
        <Show when={sellers}>
          <Stack
            sx={{
              gap: 0.5,
              px: 2,
              pt: 1,
              pb: 2,
              borderTop: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: 13 }}
            >
              Sellers
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {sellers}
            </Typography>
          </Stack>
        </Show>
      </Stack>
    </Box>
  );
};
